import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldAlert, AlertTriangle, CalendarClock, Building2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  useFinopsAlertsFacets,
  useFinopsAlertsList,
  useFinopsAlertsSummary,
} from '@/hooks/useFinops';
import { StatCard, EmptyState, LoaderBlock, fmtNum } from '@/components/finops/shared';
import { AlertsFilterBar } from '@/components/finops/AlertsFilterBar';
import { AlertsTable } from '@/components/finops/AlertsTable';
import {
  alertsEmptyKind,
  filtersToParams,
  fmtDateUTC,
  parseAlertFilters,
  type AlertsFilterState,
} from '@/components/finops/alerts-format';

/**
 * Technology → FinOps → Alerts. Filters live in the URL (shareable, survive reload);
 * they ARE the infinite query's key, so changing one starts a fresh first page and the
 * module's 5-minute staleTime keeps revisits free. `?alert=<id>` expands and scrolls to
 * that row when it is among the loaded pages — the Overview panel links with default
 * filters and the same ordering, so the row is on page 1 unless it changed in between.
 */
export function FinOpsAlertsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsKey = searchParams.toString();
  const filters = useMemo(() => parseAlertFilters(new URLSearchParams(paramsKey)), [paramsKey]);
  const focusId = searchParams.get('alert');

  const summary = useFinopsAlertsSummary();
  const facets = useFinopsAlertsFacets();
  const list = useFinopsAlertsList(filters);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set(focusId ? [focusId] : []));
  useEffect(() => {
    if (focusId) setExpandedIds((prev) => (prev.has(focusId) ? prev : new Set(prev).add(focusId)));
  }, [focusId]);

  const toggle = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // Scroll once per focus id, whenever the row first mounts (page 1 or after Load more).
  const scrolledFor = useRef<string | null>(null);
  const focusRef = useCallback((el: HTMLTableRowElement | null) => {
    if (el && focusId && scrolledFor.current !== focusId) {
      scrolledFor.current = focusId;
      el.scrollIntoView({ block: 'center' });
    }
  }, [focusId]);

  const setFilters = (next: AlertsFilterState) => setSearchParams(filtersToParams(next), { replace: true });
  const dismissFocus = () => {
    const sp = new URLSearchParams(searchParams);
    sp.delete('alert');
    setSearchParams(sp, { replace: true });
  };

  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const feedSince = summary.data?.healthFeedSince ?? null;
  const loading = summary.isLoading || list.isLoading;
  const focusMissing =
    !!focusId && !!feedSince && !loading && !list.isError && !items.some((a) => a.id === focusId);

  const s = summary.data;

  let body: React.ReactNode;
  if (loading) {
    body = <LoaderBlock />;
  } else if (summary.isError || list.isError) {
    body = (
      <EmptyState
        title={t('finops.alerts.empty.load_error.title')}
        description={t('finops.alerts.empty.load_error.description')}
      />
    );
  } else if (items.length > 0) {
    body = (
      <AlertsTable
        items={items}
        expandedIds={expandedIds}
        onToggle={toggle}
        focusId={focusId}
        focusRef={focusRef}
      />
    );
  } else {
    const kind = alertsEmptyKind(feedSince, filters);
    body =
      kind === 'feed_inactive' ? (
        <EmptyState
          title={t('finops.alerts.empty.feed_inactive.title')}
          description={t('finops.alerts.empty.feed_inactive.description')}
        />
      ) : kind === 'no_open' ? (
        <EmptyState
          title={t('finops.alerts.empty.no_open.title')}
          description={t('finops.alerts.empty.no_open.description', { date: fmtDateUTC(feedSince!) })}
        />
      ) : (
        <div className="space-y-3">
          <EmptyState
            title={t('finops.alerts.empty.no_results.title')}
            description={t('finops.alerts.empty.no_results.description')}
          />
          <div className="flex justify-center">
            <Button variant="outline" size="sm" onClick={() => setSearchParams({}, { replace: true })}>
              {t('finops.alerts.filters.clear')}
            </Button>
          </div>
        </div>
      );
  }

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-100 text-rose-600 dark:bg-rose-900/30 shrink-0 mt-0.5">
          <ShieldAlert className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('finops.alerts.page.title')}</h1>
          <p className="text-muted-foreground text-sm mt-0.5">{t('finops.alerts.page.description')}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={ShieldAlert}
          tone="rose"
          label={t('finops.alerts.stats.open_critical')}
          value={fmtNum(s?.openCritical)}
          sub={t('finops.alerts.stats.sub_open')}
        />
        <StatCard
          icon={AlertTriangle}
          tone="amber"
          label={t('finops.alerts.stats.open_warning')}
          value={fmtNum(s?.openWarning)}
          sub={t('finops.alerts.stats.sub_open')}
        />
        <StatCard
          icon={CalendarClock}
          tone="purple"
          label={t('finops.alerts.stats.deadlines_30d')}
          value={fmtNum(s?.deadlinesNext30d)}
          sub={t('finops.alerts.stats.sub_open')}
        />
        <StatCard
          icon={Building2}
          tone="blue"
          label={t('finops.alerts.stats.accounts_affected')}
          value={fmtNum(s?.accountsAffected)}
          sub={t('finops.alerts.stats.sub_open')}
        />
      </div>

      <AlertsFilterBar filters={filters} facets={facets.data} onChange={setFilters} />

      {focusMissing && (
        <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <span>{t('finops.alerts.focus.not_found')}</span>
          <button
            type="button"
            onClick={dismissFocus}
            aria-label={t('finops.alerts.focus.dismiss')}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {body}

      {list.hasNextPage && items.length > 0 && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => list.fetchNextPage()}
            disabled={list.isFetchingNextPage}
          >
            {t('finops.alerts.load_more')}
          </Button>
        </div>
      )}

      <p className="text-xs text-muted-foreground">{t('finops.alerts.page.footnote')}</p>
    </div>
  );
}
